import React, { useEffect, useState, useCallback } from "react";
import Breadcrumb from "../../components/common/Breadcrumb";
import ReactDataTable from "react-data-table-component";
import { IconCalendar } from "@tabler/icons-react";
import {
    getLoan,
    getLoanType,
    getEmployee,
    InsertLoanObj,
    getDesignation
} from "../../services/dataService";
import Loading from "../../components/common/Loading";
import toast from "react-hot-toast";
import { Link, useBeforeUnload } from "react-router-dom";

export default function LoanAmountEdit() {
    const UID = localStorage.getItem("userId");
    const [loading, setLoading] = useState(false);
    const [allLoan, setAllLoan] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    });
    const [loanType, setLoanType] = useState([]);
    const [employee, setEmployee] = useState([]);
    const [designation, setDesignation] = useState([]);
    const [amountChanges, setAmountChanges] = useState({});

    // Navigation confirmation
    useBeforeUnload(
        useCallback(() => {
            if (Object.keys(amountChanges).length > 0) {
                return "You have unsaved changes. If you leave, all changes will be reset. Are you sure?";
            }
        }, [amountChanges])
    );

    // Fetch initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const [loanTypeRes, employeeRes, designationRes, loanRes] = await Promise.all([
                    getLoanType(UID),
                    getEmployee(UID),
                    getDesignation(UID),
                    getLoan(UID)
                ]);

                setLoanType(loanTypeRes.data.data);
                setEmployee(employeeRes.data.data);
                setDesignation(designationRes.data.data);
                setAllLoan(loanRes.data.data);
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [UID]);

    // Process and filter data
    useEffect(() => {
        if (allLoan.length === 0 || !selectedMonth) {
            setFilteredData([]);
            return;
        }

        const selectedDate = new Date(selectedMonth);
        const monthName = selectedDate.toLocaleDateString('en-US', { month: 'long' });
        const year = selectedDate.getFullYear().toString();

        const employeeLoans = {};
        allLoan?.forEach(loan => {
            if (loan.LoanType && loan.EmployeeId) {
                const loanTypeData = loanType.find(lt => lt.Id === loan.LoanType);
                if (loanTypeData?.Name !== 'Society Loan') return;

                const key = `${loan.EmployeeId}_${loan.LoanType}`;
                const isCurrentMonth = loan.month === monthName && loan.Year === year;

                if (!employeeLoans[key]) {
                    employeeLoans[key] = {
                        ...loan,
                        Amount: isCurrentMonth ? loan.Amount : 0,
                        month: monthName,
                        Year: year,
                        isEditable: true
                    };
                } else if (isCurrentMonth) {
                    employeeLoans[key] = {
                        ...loan,
                        isEditable: true
                    };
                }
            }
        });

        const processedData = Object.values(employeeLoans).map(loan => {
            // const employeeData = employee.find(e => e.Id === loan.EmployeeId);
            return {
                ...loan,
                EmployeeName: loan?.EmployeeName || `Unknown`,
                EmployeeCode: loan.EmployeeCode || "-",
                DesignationName: designation.find(d => d.Id == loan.DesignationId)?.Name || "-"
            };
        });

        setFilteredData(processedData);
    }, [allLoan, selectedMonth, loanType, employee, designation]);

    // Handle amount change
    const handleAmountChange = (rowId, value) => {
        setAmountChanges(prev => ({
            ...prev,
            [rowId]: parseFloat(value) || 0
        }));
    };

    // Submit all changes
    const handleSubmitAll = async () => {
        if (Object.keys(amountChanges).length === 0) {
            toast.error("No changes to submit");
            return;
        }

        try {
            setLoading(true);
            const selectedDate = new Date(selectedMonth);
            const monthName = selectedDate.toLocaleDateString('en-US', { month: 'long' });
            const year = selectedDate.getFullYear().toString();

            const updatedLoans = filteredData
                .filter(row => amountChanges[row.Id] !== undefined)
                .map(row => ({
                    ...row,
                    Id: row.month === monthName && row.Year === year && row.Amount > 0 ? row.Id : "",
                    Amount: amountChanges[row.Id],
                    month: monthName,
                    Year: year,
                    UserId: UID
                }));

            const response = await InsertLoanObj({ loanData: updatedLoans });

            if (response.data.Status === 201) {
                const loanRes = await getLoan(UID);
                setAllLoan(loanRes.data.data);
                setAmountChanges({});
                toast.success("All changes saved successfully");
            } else {
                toast.error(response.message || "Failed to save changes");
            }
        } catch (error) {
            console.error("Error saving changes:", error);
            toast.error("Error saving changes");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            name: "Employee",
            selector: row => row.EmployeeName,
            sortable: true,
            grow: 2,
        },
        {
            name: "Code",
            selector: row => row.EmployeeCode,
            sortable: true,
            minWidth: "120px"
        },
        {
            name: "Designation",
            selector: row => row.DesignationName,
            sortable: true,
            minWidth: "550px"
        },
        // {
        //     name: "Month",
        //     selector: row => row.month,
        //     sortable: true,
        //     width: "140px",
        //     minWidth: "140px"
        // },
        // {
        //     name: "Year",
        //     selector: row => row.Year,
        //     sortable: true,
        //     width: "100px",
        //     minWidth: "100px"
        // },
        {
            name: "Amount",
            cell: row => (
                <input
                    type="number"
                    value={amountChanges[row.Id] !== undefined ? amountChanges[row.Id] : row.Amount}
                    onChange={(e) => handleAmountChange(row.Id, e.target.value)}
                    className="w-24 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    disabled={loading}
                />
            ),
            width: "150px",
            minWidth: "150px"
        }
    ];

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                <div>
                    <Link
                        to="/loan"
                        className="text-blue-600 hover:text-blue-800"
                        onClick={(e) => {
                            if (Object.keys(amountChanges).length > 0) {
                                if (!window.confirm("You have unsaved changes. If you leave, all changes will be reset. Are you sure?")) {
                                    e.preventDefault();
                                }
                            }
                        }}
                    >
                        <Breadcrumb title="Back to Loan" />
                    </Link>
                    <h1 className="text-2xl font-bold mt-2">Monthly Society Loan Amounts</h1>
                    <p className="text-sm text-gray-600">
                        Edit society loan amounts for specific months
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    {/* Month Selection */}
                    <div className="flex items-center gap-2 bg-white p-2 rounded border">
                        <IconCalendar className="text-blue-500" />
                        <input
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="px-3 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Search Input */}
                    <div className="flex items-center gap-2 bg-white p-2 rounded border">
                        <input
                            type="text"
                            placeholder="Search by Name, Code or Designation ..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            className="px-3 py-2 border rounded w-[300px] max-w-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {selectedMonth && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <div>
                        <h3 className="font-medium text-blue-800">
                            Editing Society Loans for: {new Date(selectedMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h3>
                        <p className="text-sm text-blue-600">
                            {filteredData.length} employees with society loans
                        </p>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-x-auto overflow-y-hidden">
                {loading ? (
                    <div className="p-8 flex justify-center">
                        <Loading />
                    </div>
                ) : (
                    <>
                        {selectedMonth ? (
                            <>
                                <ReactDataTable
                                    columns={columns}
                                    data={filteredData.filter(item => {
                                        const designationName = designation.find(d => d.Id === item.DesignationId)?.Name || "-";
                                        return (
                                            item.EmployeeName.toLowerCase().includes(filterText.toLowerCase()) ||
                                            item.EmployeeCode.toLowerCase().includes(filterText.toLowerCase()) ||
                                            designationName.toLowerCase().includes(filterText.toLowerCase())
                                        );
                                    })}
                                    pagination
                                    highlightOnHover
                                    striped
                                    noDataComponent={
                                        <div className="p-4 text-center text-gray-500">
                                            No society loans found for selected month
                                        </div>
                                    }
                                />
                                <div className="p-4 border-t">
                                    <button
                                        onClick={handleSubmitAll}
                                        disabled={loading || Object.keys(amountChanges).length === 0}
                                        className="px-4 py-2 bg-blue text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
                                    >
                                        {loading ? 'Submitting...' : 'Submit All Changes'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="p-8 text-center text-gray-500">
                                <IconCalendar size={48} className="mx-auto text-gray-300 mb-4" />
                                <h3 className="text-lg font-medium">Select a month to view and edit society loan amounts</h3>
                                <p className="mt-2">Choose a month using the selector above</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium mb-2">Instructions:</h3>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                    <li>Select a month to view and edit society loan amounts</li>
                    <li>Enter amounts directly in the input fields</li>
                    <li>Click "Submit All Changes" to save all modifications</li>
                    <li>Unsaved changes will be lost if you navigate away</li>
                </ul>
            </div>
        </div>
    );
}